/**
 * Strip the SSO callback's query out of the address bar BEFORE any analytics SDK
 * boots, and before any third-party container script can read
 * `window.location.href`.
 *
 * The backend's SSO redirect lands as
 * `/auth/callback?sso=success&code=<one-time-uuid>`, and that code exchanges for
 * a customer session (60s TTL, single use). Two sinks read the raw address bar:
 *   - `gtm.client.ts` calls `createGtm({ loadScript: true })`, which appends
 *     `gtm.js` immediately. Any All Pages / GA4 / HubSpot / LinkedIn tag inside
 *     that container reads `window.location.href` — third-party code we do not
 *     control and cannot redact after the fact.
 *   - `mixpanel.client.ts` initialises the SDK, which attaches `$current_url`
 *     from `window.location.href` to EVERY event, not just pageviews.
 *
 * Why this can't live in the page: `callback.vue` strips the code in
 * `onMounted`, which runs long after plugins initialise.
 *
 * The whole query goes, not a list of named params — nothing on this route is
 * worth keeping in the URL, and matching by name is the thing that silently
 * fails when a new param appears. The router's own sinks (`to.fullPath`) are not
 * covered by this: they are gated on the same route in `gtm.client.ts` and
 * `mixpanel-pageviews.client.ts`.
 *
 * Prefix `01-` so this runs after `00-sanitize-config` but before every
 * analytics plugin (Nuxt loads plugins in alphabetical order).
 *
 * Safe for the callback page: `callback.vue` reads the code from `route.query`,
 * which vue-router resolved at navigation time and holds independently of
 * `history`. Rewriting the address bar does not disturb it — the same reason the
 * page's own later `replaceState` works.
 *
 * Login as lands on any page with `?loginAs=` (a live customer JWT and the staff
 * member's id). Only that param goes, for the same reasons; the auth store reads
 * it from `route.query` during boot, so it leaves the route only after that.
 */
export default defineNuxtPlugin((nuxtApp) => {
  if (!import.meta.client) return;

  const params = new URLSearchParams(window.location.search);
  if (params.has("loginAs")) {
    params.delete("loginAs");
    const query = params.toString();
    window.history.replaceState(
      window.history.state,
      "",
      window.location.pathname + (query ? `?${query}` : "") + window.location.hash,
    );
    // Nuxt's router writes the initial URL back on app:created, and pages rebuild their URL
    // from route.query, so take it out of the route too. Hooks run in order, the router's first.
    nuxtApp.hooks.hookOnce("app:created", async () => {
      const route = nuxtApp.$router.currentRoute.value;
      if (!("loginAs" in route.query)) return;
      const rest = { ...route.query };
      delete rest.loginAs;
      await nuxtApp.$router.replace({ path: route.path, query: rest, hash: route.hash });
    });
  }

  if (window.location.pathname !== "/auth/callback" || !window.location.search) return;

  // Preserve history.state or vue-router loses scroll restoration and
  // back/forward linkage for this entry.
  window.history.replaceState(window.history.state, "", window.location.pathname);
});
